/* FP-VIN resolver v2: VIN -> {truck, truckKey, engine (parent/child/era), open questions}.
   Deterministic - NHTSA vPIC row (when reachable) + local per-make position maps
   (fp-vin-maps.json, built by scripts/build_vin_maps.py from FP_VIN_V2_MEMO research).
   Library only: exposes window.FPVIN, never touches the DOM.
   Doctrine (FP_VIN_V2_MEMO par.2/9): vPIC ErrorCode 1 = check-digit warning, keep decoding;
   era-scoped position map beats the API on engine disagreement; what the VIN cannot say
   ships as open_questions[] - ask ONE question instead of guessing. */
(function () {
  var root = typeof window !== 'undefined' ? window : this;
  if (root.FPVIN) return;
  var mapsPromise = null, MAPS = null;
  var VPIC = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/';

  function nowYear() { return root.FPVIN && root.FPVIN._nowYear ? root.FPVIN._nowYear : new Date().getFullYear(); }
  function maxYear() { return nowYear() + 1; }

  /* ---------- maps ---------- */
  function load(src) {
    if (!mapsPromise) {
      mapsPromise = fetch(src).then(function (r) { return r.json(); }).then(function (m) { MAPS = m; return m; });
    }
    return mapsPromise;
  }
  function injectMaps(m) { MAPS = m; mapsPromise = Promise.resolve(m); return m; }

  /* ---------- small utils ---------- */
  function titleCase(s) {
    return String(s || '').toLowerCase().replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }
  function intOr(v, d) { var n = parseInt(v, 10); return isNaN(n) ? d : n; }
  function scopeRow(scopes, year) {
    if (!scopes) return undefined;
    for (var i = 0; i < scopes.length; i++) {
      var s = scopes[i], f = s.from || 0, t = s.to === null || s.to === undefined ? 9999 : s.to;
      if (year >= f && year <= t) return s;
    }
    return undefined; /* code known, era not covered */
  }
  function scopePick(scopes, year) {
    var s = scopeRow(scopes, year);
    return s ? s.engine : undefined;
  }

  /* ---------- vocab (embedded copy of fp-ymm-data) ---------- */
  function vocabMake(name) {
    if (!name) return null;
    var up = String(name).trim().toUpperCase();
    var ali = MAPS.vocab.aliases.makes[up];
    var target = ali || null;
    if (!target) {
      for (var i = 0; i < MAPS.vocab.makes.length; i++) {
        if (MAPS.vocab.makes[i].name.toUpperCase() === up) { target = MAPS.vocab.makes[i].name; break; }
      }
    }
    if (!target) return null;
    for (var j = 0; j < MAPS.vocab.makes.length; j++) {
      if (MAPS.vocab.makes[j].name === target) return MAPS.vocab.makes[j];
    }
    return null;
  }
  function vocabModel(makeObj, modelName) {
    if (!makeObj || !modelName) return null;
    for (var i = 0; i < makeObj.models.length; i++) {
      if (makeObj.models[i].name.toUpperCase() === String(modelName).toUpperCase()) return makeObj.models[i];
    }
    return null;
  }
  function normModelName(raw) {
    /* "VNL (4)" -> {name:"VNL", genHint:4}; "T-680" -> T680; unknown stays cleaned raw */
    if (!raw) return { name: null, genHint: null };
    var s = String(raw).trim();
    var hint = null, m = s.match(/\((\d)\)\s*$/);
    if (m) { hint = intOr(m[1], null); s = s.replace(/\s*\(\d\)\s*$/, ''); }
    var up = s.toUpperCase().replace(/\s+/g, ' ');
    var prefixes = MAPS.vocab.aliases.modelPrefixes;
    for (var i = 0; i < prefixes.length; i++) {
      if (up.indexOf(prefixes[i][0]) === 0) return { name: prefixes[i][1], genHint: hint };
    }
    return { name: titleCase(s), genHint: hint };
  }
  function genForYear(modelObj, year) {
    for (var i = 0; i < modelObj.generations.length; i++) {
      var g = modelObj.generations[i];
      var to = g.to === null ? maxYear() : g.to;
      if (year >= g.from && year <= to) return g;
    }
    return null;
  }

  /* ---------- emission era + engine keys ---------- */
  function eraFor(year) {
    if (!year) return null;
    for (var i = 0; i < MAPS.eras.length; i++) {
      var e = MAPS.eras[i], to = e.to === null ? 9999 : e.to;
      if (year >= e.from && year <= to) return e;
    }
    return null;
  }
  function engineFromToken(token, year) {
    /* token: canonical parent | CUMMINS_15L | BEV | OTHER:label | null */
    if (token === null || token === undefined) return null;
    if (token === 'BEV') return { other: 'Electric (BEV)' };
    if (typeof token === 'string' && token.indexOf('OTHER:') === 0) return { other: token.slice(6) };
    var parent = token;
    if (token === 'CUMMINS_15L') {
      parent = (year && year <= MAPS.engines.cummins15.isx15_to) ? 'Cummins ISX15' : 'Cummins X15';
    }
    if (MAPS.engines.parents.indexOf(parent) === -1) return { other: String(token) };
    var era = eraFor(year);
    var win = MAPS.engines.windows[parent] || {};
    var inWin = year && year >= (win.from || 0) && year <= (win.to === null || win.to === undefined ? 9999 : win.to);
    var child = null;
    if (era && inWin) {
      child = parent + ' (' + era.code + ' ' + era.from + (era.to === null ? '+' : '-' + era.to) + ')';
    }
    return { parent: parent, child: child, era: era && inWin ? era.code : null };
  }
  function normApiEngine(engineModel, engineManufacturer, year) {
    /* returns token (same grammar as position maps) or null */
    var s = ((engineManufacturer || '') + ' ' + (engineModel || '')).toUpperCase();
    if (!s.replace(/\s/g, '')) return null;
    if (/\bDD[\s-]?16\b/.test(s)) return 'Detroit DD16';
    if (/\bDD[\s-]?15\b/.test(s)) return 'Detroit DD15';
    if (/\bDD[\s-]?13\b/.test(s)) return 'Detroit DD13';
    if (/\bMP[\s-]?8\b/.test(s)) return 'Mack MP8';
    if (/\bMP[\s-]?7\b/.test(s)) return 'Mack MP7';
    if (/\bMX[\s\/-]*13\b/.test(s) || /\bMX\b(?![\s\/-]*11)/.test(s)) return 'Paccar MX13';
    if (/\bMX[\s\/-]*11\b/.test(s)) return 'OTHER:PACCAR MX-11';
    if (/\bD16\b/.test(s)) return 'OTHER:Volvo D16';
    if (/SERIES\s*60|\b60\s*SERIES\b/.test(s)) return 'OTHER:Detroit Series 60';
    if (/\bISX[\s\/-]*12|X12N?\b/.test(s)) return 'OTHER:Cummins ISX12';
    /* Cummins medium-duty: 2017 rebrand split mirrors ISX15/X15 (engines.json) -
       CNG variants stay OTHER and must be tested BEFORE the diesel patterns */
    if (/\bISL[\s-]?G\b|\bL9N\b/.test(s)) return 'OTHER:Cummins ISL-G (CNG)';
    if (/\bB[\s-]?6\.7\b|\bISB\b/.test(s)) return (year && year >= 2017) ? 'Cummins B6.7' : 'OTHER:Cummins ISB';
    if (/\bL[\s-]?9\b(?![0-9])|\bISL\b/.test(s)) return (year && year >= 2017) ? 'Cummins L9' : 'OTHER:Cummins ISL';
    if (/\bN[\s-]?13\b/.test(s)) return 'Navistar N13';
    if (/\bX[\s-]?15\b|\bISX(?:[\s-]?15)?\b/.test(s)) return 'CUMMINS_15L';
    if (/\bD13\b/.test(s)) return 'Volvo D13';
    if (/\bD11\b/.test(s)) return 'Volvo D11';
    return 'OTHER:' + titleCase(String(engineModel || engineManufacturer).trim());
  }

  /* ---------- local VIN parse (pure position maps, no network) ---------- */
  function checkDigitOk(vin) {
    var cd = MAPS.check_digit, sum = 0;
    for (var i = 0; i < 17; i++) {
      var ch = vin[i], v = /\d/.test(ch) ? intOr(ch, 0) : cd.values[ch];
      if (v === undefined) return false;
      sum += v * cd.weights[i];
    }
    var r = sum % 11, want = r === 10 ? cd.remainder_10 : String(r);
    return vin[8] === want;
  }
  function yearFromPos10(ch) {
    var neu = MAPS.years.cycle_2010[ch], old = MAPS.years.cycle_1980[ch];
    if (neu === undefined) return { year: null, ambiguous: false };
    if (neu <= maxYear()) {
      return { year: neu, ambiguous: old !== undefined && old >= 1995 };
    }
    return { year: old !== undefined ? old : null, ambiguous: false };
  }
  function parse(vin) {
    var raw = String(vin || '').toUpperCase().replace(/[\s-]/g, '');
    if (raw.length !== 17 || /[IOQ]/.test(raw) || /[^A-Z0-9]/.test(raw)) {
      return { valid: false, reason: 'format', vin: raw };
    }
    var wmi = raw.slice(0, 3);
    var y = yearFromPos10(raw[9]);
    var p = {
      valid: true, vin: raw, wmi: wmi, make: null, bucket: null, modelLine: null, cab: null,
      year: y.year, yearAmbiguous: y.ambiguous, engineLocal: undefined, hpBand: null,
      incomplete: false, checkDigitOk: checkDigitOk(raw), trailerLikely: false
    };
    var mk = MAPS.makes, division;
    if (mk.volvo.wmi_complete.indexOf(wmi) >= 0 || mk.volvo.wmi_incomplete.indexOf(wmi) >= 0) {
      p.bucket = 'volvo'; p.make = mk.volvo.name;
      p.incomplete = mk.volvo.wmi_incomplete.indexOf(wmi) >= 0;
      p.modelLine = mk.volvo.model_pos4[raw[3]] || null;
      p.engineLocal = scopePick(mk.volvo.engine_pos7[raw[6]], p.year);
      p.hpBand = mk.volvo.hp_pos8[raw[7]] || null;
    } else if (mk.freightliner.wmi_complete.indexOf(wmi) >= 0 || mk.freightliner.wmi_incomplete.indexOf(wmi) >= 0) {
      p.bucket = 'freightliner'; p.make = mk.freightliner.name;
      p.incomplete = mk.freightliner.wmi_incomplete.indexOf(wmi) >= 0;
      var pair56 = mk.freightliner.model_pairs_pos56[raw.slice(4, 6)];
      if (pair56) { p.modelLine = pair56.m; p.cab = pair56.cab || null; }
      var pair78 = mk.freightliner.engine_pairs_pos78;
      p.engineLocal = pair78.hasOwnProperty(raw.slice(6, 8)) ? pair78[raw.slice(6, 8)] : undefined;
    } else if (mk.paccar.divisions[wmi]) {
      division = mk.paccar.divisions[wmi];
      p.bucket = 'paccar'; p.make = division;
      p.incomplete = mk.paccar.wmi_incomplete.indexOf(wmi) >= 0;
      p.modelLine = (mk.paccar.model_pos4[division] || {})[raw[3]] || null;
      p.engineLocal = scopePick(mk.paccar.engine_pos6[raw[5]], p.year);
    } else if (MAPS.mack.wmis.indexOf(wmi) >= 0) {
      p.bucket = 'mack'; p.make = MAPS.mack.name;
      /* Mack VDS (vPIC sweeps 2026-07-14): MODEL = pos4-5 PAIR (era-scoped -
         'AN' is CHU Pinnacle pre-2018, Anthem 2018+), ENGINE = pos6 DIGIT.
         Tables apply to the position_wmis (1M1/1M2); other Mack WMIs stay
         make-only and ride the vPIC API path. */
      if (MAPS.mack.position_wmis && MAPS.mack.position_wmis.indexOf(wmi) >= 0) {
        var mrow = scopeRow((MAPS.mack.model_pairs_pos45 || {})[raw.slice(3, 5)], p.year);
        p.modelLine = mrow ? mrow.model : null;
        p.engineLocal = scopePick((MAPS.mack.engine_pos6 || {})[raw[5]], p.year);
      }
    } else if (MAPS.trailer_wmis[wmi]) {
      p.trailerLikely = true; p.make = MAPS.trailer_wmis[wmi];
    }
    return p;
  }

  /* ---------- vPIC row helpers ---------- */
  function apiErrCode(row) {
    var m = String(row && row.ErrorCode !== undefined && row.ErrorCode !== null ? row.ErrorCode : '').match(/\d+/);
    return m ? intOr(m[0], 99) : 99;
  }
  function apiUsable(row) {
    if (!row) return false;
    var c = apiErrCode(row);
    return c === 0 || c === 1;
  }
  function cleanTrailerMake(raw) {
    var s = String(raw || '').toUpperCase();
    var cut = s.search(/\bTRAILERS?\b/);
    if (cut > 0) s = s.slice(0, cut);
    s = s.replace(/\b(LLC|INC|CO|COMPANY|CORP|CORPORATION|LIMITED|PARTNERSHIP|MANUFACTURING|MANUFACTURER)\b/g, ' ');
    return titleCase(s.replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim());
  }

  /* ---------- the resolver (memo par.9) ---------- */
  function resolve(vin, row) {
    if (!MAPS) return { valid: false, reason: 'maps-not-loaded' };
    var p = null;
    if (vin !== null && vin !== undefined && String(vin) !== '') {
      p = parse(vin);
      if (!p.valid) return p;
    }
    var usable = apiUsable(row);
    if (!p && !usable) return { valid: false, reason: 'no-input' };

    var flags = {}, questions = [];
    if (usable && apiErrCode(row) === 1) flags.checkDigitWarn = true;
    if (p && p.checkDigitOk === false && !usable) flags.checkDigitWarn = true;
    if (!usable && p) flags.offline = true;
    if (p && p.incomplete) flags.incomplete = true;
    if (p && p.yearAmbiguous) flags.yearAmbiguous = true;
    if (usable && /incomplete/i.test(String(row.BodyClass || ''))) flags.incomplete = true;
    if (usable && /model\s*year.*incorrect/i.test(String(row.AdditionalErrorText || ''))) flags.newGenWarn = true;

    /* trailer branch - api rich fields when reachable, WMI-only offline.
       body = normalized token (flatbed/van/reefer/...) from TrailerBodyType +
       OtherTrailerInfo via the compiled trailer_recos patterns - drives the
       accessory recommendations (Nicolas 2026-07-07: use ALL vPIC data). */
    if ((usable && /trailer/i.test(String(row.BodyClass || ''))) || (p && p.trailerLikely)) {
      var tyear = usable ? intOr(row.ModelYear, p ? p.year : null) : (p ? p.year : null);
      var bodyText = usable
        ? ((row.TrailerBodyType || '') + ' ' + (row.OtherTrailerInfo || '')).replace(/\s+/g, ' ').trim()
        : '';
      var body = null;
      if (bodyText && MAPS.trailer_recos && MAPS.trailer_recos.patterns) {
        var pats = MAPS.trailer_recos.patterns;
        for (var bi = 0; bi < pats.length; bi++) {
          if (new RegExp(pats[bi][1], 'i').test(bodyText)) { body = pats[bi][0]; break; }
        }
      }
      return {
        valid: true, kind: 'trailer', vin: p ? p.vin : null,
        trailer: {
          make: usable ? cleanTrailerMake(row.Make) : (p ? p.make : null),
          year: tyear,
          axles: usable ? intOr(row.Axles, null) : null,
          type: usable ? (row.TrailerType || null) : null,
          length: usable ? intOr(row.TrailerLength, null) : null,
          body: body,
          bodyRaw: bodyText || null
        },
        truck: null, truckKey: null, engine: null, hpBand: null,
        confidence: usable ? 'api-only' : 'map-only',
        flags: flags, questions: ['trailer']
      };
    }

    /* truck: make/model/year - API preferred for display, map fills gaps */
    var year = usable ? intOr(row.ModelYear, p ? p.year : null) : (p ? p.year : null);
    var makeName = null, modelInfo = { name: null, genHint: null };
    if (usable && row.Make) {
      var vm = vocabMake(row.Make);
      makeName = vm ? vm.name : titleCase(row.Make);
    } else if (p && p.make) { makeName = p.make; }
    if (usable && row.Model) {
      modelInfo = normModelName(row.Model);
    } else if (p && p.modelLine) { modelInfo = { name: p.modelLine, genHint: null }; }

    var makeObj = vocabMake(makeName);
    var modelObj = vocabModel(makeObj, modelInfo.name);
    var gen = modelObj && year ? genForYear(modelObj, year) : null;
    var truckKey = gen ? gen.value : null;

    /* engine: era-scoped position map wins on disagreement (vPIC E+K@2019 case) */
    var mapToken = p ? p.engineLocal : undefined;
    var apiToken = usable ? normApiEngine(row.EngineModel, row.EngineManufacturer, year) : null;
    var token, confidence;
    var mapHas = mapToken !== undefined && mapToken !== null;
    var apiHas = apiToken !== null;
    if (mapHas && apiHas) {
      token = mapToken;
      confidence = engineTokensAgree(mapToken, apiToken, year) ? 'map+api' : 'map-over-api';
    } else if (mapHas) { token = mapToken; confidence = 'map-only'; }
    else if (apiHas) { token = apiToken; confidence = 'api-only'; }
    else { token = null; confidence = usable ? 'api-only' : 'map-only'; }

    var engine = engineFromToken(token, year);
    if (token === 'BEV' || (p && p.engineLocal === 'BEV')) flags.bev = true;

    var era = eraFor(year);
    if (era && year === era.from && engine && engine.parent) flags.eraBoundary = true;

    /* open questions - ordered, UI shows the first only */
    if (flags.bev) questions.push('bev');
    if (!engine && !flags.bev) questions.push('engine-unknown');
    if (!truckKey) questions.push('truck-not-in-vocab');

    return {
      valid: true, kind: 'truck', vin: p ? p.vin : null,
      truck: { make: makeName, model: modelInfo.name, year: year },
      genHint: modelInfo.genHint,
      cab: (p && p.cab) || (usable ? (row.Trim || row.Series || null) : null) || null,
      truckKey: truckKey,
      engine: engine,
      hpBand: p ? p.hpBand : null,
      confidence: confidence,
      flags: flags,
      questions: questions
    };
  }
  function engineTokensAgree(a, b, year) {
    var ea = engineFromToken(a, year), eb = engineFromToken(b, year);
    if (!ea || !eb) return false;
    if (ea.parent && eb.parent) return ea.parent === eb.parent;
    if (ea.other && eb.other) return ea.other === eb.other;
    return false;
  }

  /* ---------- network path ---------- */
  function fetchAndResolve(vin) {
    var p = parse(vin);
    if (!p.valid) return Promise.resolve(p);
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 6000) : null;
    return fetch(VPIC + encodeURIComponent(p.vin) + '?format=json', ctrl ? { signal: ctrl.signal } : {})
      .then(function (r) { return r.json(); })
      .then(function (j) { return resolve(p.vin, (j && j.Results && j.Results[0]) || null); })
      .catch(function () { return resolve(p.vin, null); })
      .then(function (res) { if (timer) clearTimeout(timer); return res; });
  }

  root.FPVIN = {
    load: load,
    parse: parse,
    resolve: resolve,
    fetchAndResolve: fetchAndResolve,
    maps: function () { return MAPS; },
    _injectMaps: injectMaps,
    _nowYear: null
  };
})();
